import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { resolveLoginReturnTo } from '@/lib/authNavigation';
import styles from './AuthPages.module.css';

export function RequireAuth() {
  const { user, loading, setupRequired, initializationError, retryInitialization } = useAuth();
  const location = useLocation();

  if (loading) return <div className={styles.loading}>Loading session...</div>;
  if (initializationError) return <InitializationError message={initializationError} retry={retryInitialization} />;
  if (setupRequired) return <Navigate to="/setup" replace />;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <Outlet />;
}

export function RequirePasswordChanged() {
  const { user } = useAuth();
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Outlet />;
}

export function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  return user?.role === 'Admin' ? children : (
    <Navigate to="/forbidden" replace state={{ attemptedPath: `${location.pathname}${location.search}${location.hash}` }} />
  );
}

export function TradingOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  return user?.role === 'Admin' || user?.role === 'Trader' ? children : (
    <Navigate to="/forbidden" replace state={{ attemptedPath: `${location.pathname}${location.search}${location.hash}` }} />
  );
}

export function GuestOnly({ children }: { children: ReactNode }) {
  const { user, loading, setupRequired, initializationError, retryInitialization } = useAuth();
  const location = useLocation();
  if (loading) return <div className={styles.loading}>Loading session...</div>;
  if (initializationError) return <InitializationError message={initializationError} retry={retryInitialization} />;
  if (setupRequired) return <Navigate to="/setup" replace />;
  if (user) {
    const returnTo = resolveLoginReturnTo(
      (location.state as { from?: string } | null)?.from,
      location.search,
    );
    return <Navigate to={user.mustChangePassword ? '/change-password' : returnTo} replace />;
  }
  return children;
}

export function SetupOnly({ children }: { children: ReactNode }) {
  const { user, loading, setupRequired, initializationError, retryInitialization } = useAuth();
  if (loading) return <div className={styles.loading}>Loading setup...</div>;
  if (initializationError) return <InitializationError message={initializationError} retry={retryInitialization} />;
  if (user) return <Navigate to={user.mustChangePassword ? '/change-password' : '/'} replace />;
  if (!setupRequired) return <Navigate to="/login" replace />;
  return children;
}

function InitializationError({ message, retry }: { message: string; retry: () => Promise<void> }) {
  return (
    <main className={styles.authPage}>
      <div className={styles.authContent}>
        <Alert variant="danger">Unable to initialize the application: {message}</Alert>
        <Button className={styles.submit} onClick={() => void retry()}>Retry</Button>
      </div>
    </main>
  );
}
